import React from "react";

/** A field with nothing in it says so, rather than leaving a gap. */
export const UNSET = "-";

/**
 * Label followed by its value, the repeating unit of a record read back — in
 * the two shapes the same pair has to take.
 *
 * On a phone it is a row of a grouped list: the label at the left edge, the
 * answer against the right, a rule between it and the next one. That is what a
 * phone does with a record, and it is what the desktop arrangement cannot be
 * shrunk into — three pairs spread across a card wrap into a ragged block the
 * moment the card is 360px wide, and which label a value belongs to stops being
 * obvious.
 *
 * From `md` up it is the inline pair the design lays across the card, and the
 * row's own padding and rule are handed back to the container.
 *
 * Shared rather than written per screen: the profile and the brand are read
 * back on two screens of the same app, and a reader who checks their phone
 * number on one and their account number on the other should not be able to
 * tell the two apart.
 */
export const RecordField: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-3 md:justify-start md:gap-2 md:py-0">
    <span className="flex-shrink-0 text-[13px] 3xl:text-[14px] text-ink-muted">
      {label}
    </span>
    <span className="min-w-0 text-right text-[14px] 3xl:text-[15px] font-medium text-ink md:text-left">
      {value}
    </span>
  </div>
);

/**
 * The class list a run of `RecordField`s is laid out with: a divided column on
 * a phone, the design's spread row from `md` up. The dividers have to be
 * turned off with the column — in a row they would draw a line down the left
 * of every field but the first.
 */
export const RECORD_FIELD_ROWS =
  "flex flex-col divide-y divide-line md:flex-row md:flex-wrap " +
  "md:gap-y-3 md:divide-y-0 md:py-4";

/**
 * One card of a record screen, with the caption a phone puts over a group of
 * rows.
 *
 * The caption is a phone's only way of saying what a group of rows is about:
 * on a wide screen the cards are read at a glance as parts of one record, and
 * stacked full width they are just rows going down the page. It is left off
 * from `md` up, where the design has no captions and does not need them, and
 * off any card whose own header already says what it is.
 */
export const RecordCard: React.FC<{
  title?: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section>
    {title && (
      <h3 className="mb-2 px-1 text-[13px] text-ink-muted md:hidden">{title}</h3>
    )}
    <div className="card-surface border border-line bg-white">{children}</div>
  </section>
);

/**
 * The one answer that is not a row: a value that runs to a line and a half on
 * a phone — an address, a bank's full name — squeezed into the right of a row
 * would be set three or four words to a line. Its label goes above it instead,
 * which is what a phone does with a long value anyway.
 */
export const RecordBlock: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="px-5 py-3.5 md:px-6 md:py-4">
    <div className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</div>
    <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
      {value}
    </div>
  </div>
);
